import { Component, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { StatoSessioneService } from '../../../shared/services/stato-sessione.service';
import { AuthService } from '../../../shared/auth/auth.service';
import { UserStoryService, AgentChatSource } from '../../../shared/templateGen/template-api.service';
import { combineLatest, Observable, of, Subject } from 'rxjs';
import { filter, map, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import Swal from 'sweetalert2';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import {
  FileRecordResponse,
  GenerateEmbeddingsResponse,
  KBSearchResult,
  KnowledgeBaseItem,
  PersistenceApiService,
} from '../../../shared/services/persistence-api.service';
import { KnowledgeBaseState, KnowledgeBaseStateService } from './knowledge-base-state.service';
import { HighlightInfo } from './document-highlighter/document-highlighter.component';
import { FileContentViewerComponent } from './file-content-viewer/file-content-viewer.component';
import { ActivatedRoute } from '@angular/router';

export interface ProcessingKnowledgeItem {
  status: 'Pending' | 'Processing' | 'Success' | 'Error';
  snippet: HighlightInfo;
  resultItems?: KnowledgeBaseItem[];
  error?: string;
}

export interface PersistedFileInfo {
  id?: string;
  name: string;
  status: 'Loading' | 'Uploading' | 'Saving' | 'Success' | 'Error' | 'Deleting' | 'Indexing';
  timestamp: Date;
  message?: string;
  content?: string;
  contentType?: 'text' | 'html' | 'docx-text' | 'pdf';
  uploadedBy?: string;
  isEmbedded?: boolean;
  contentLength?: number;
}

@Component({
  selector: 'app-knowledge-base',
  templateUrl: './knowledge-base.component.html',
  styleUrls: ['./knowledge-base.component.scss'],
})
export class KnowledgeBaseComponent implements OnInit, OnDestroy {
  @ViewChild('kiDetailModal') kiDetailModal: TemplateRef<any>;

  isChatOpen = false;
  public state$: Observable<KnowledgeBaseState> = this.stateService.state$;

  projectId = '';
  currentUserEmail = '';
  selectedModel = 'GPT-5.4 mini';

  searchQueryControl = '';
  maxIterations = 20;

  // KI da Risoluzioni Ambiguita'
  ambiguityKiItems: KnowledgeBaseItem[] = [];
  filteredKiItems: KnowledgeBaseItem[] = [];
  kiSearchQuery = '';
  kiDisplayLimit = 10;
  selectedKi: KnowledgeBaseItem | null = null;
  selectedKiStory: string | null = null;
  isEditingKi = false;
  isSavingKi = false;
  editKiData: { title: string; question: string; impact_text: string; answer: string } = { title: '', question: '', impact_text: '', answer: '' };

  private destroy$ = new Subject<void>();

  /** Files still uploading, saving, or not yet indexed */
  get pendingFiles(): PersistedFileInfo[] {
    return this.stateService.currentState.persistedFiles
      .filter(f => !f.isEmbedded || f.status === 'Uploading' || f.status === 'Saving' || f.status === 'Indexing');
  }

  /** Files that are indexed and ready */
  get indexedFiles(): PersistedFileInfo[] {
    return this.stateService.currentState.persistedFiles
      .filter(f => f.isEmbedded && f.status === 'Success');
  }

  constructor(
    private route: ActivatedRoute,
    private stateService: KnowledgeBaseStateService,
    private sessioneService: StatoSessioneService,
    private authService: AuthService,
    private userStoryService: UserStoryService,
    private persistenceApiService: PersistenceApiService,
    private modalService: NgbModal
  ) {}

  ngOnInit(): void {
    combineLatest([
      this.route.params,
      this.authService.email$
    ]).pipe(
      take(1),
      tap(([params, email]) => {
        if (params['project_id']) {
          this.projectId = params['project_id'];
          this.sessioneService.updateProjectId(this.projectId);
        }
        if (email) {
          this.currentUserEmail = email;
        }
      }),
      filter(() => {
        if (!this.projectId) {
          this.handleCriticalError('Project ID not found in URL.');
          return false;
        }
        if (!this.currentUserEmail) {
          this.handleCriticalError('User email not available.');
          return false;
        }
        return true;
      }),
      switchMap(() => of('GPT-5.4 mini')),
      take(1),
      takeUntil(this.destroy$)
    ).subscribe({
      next: (model) => {
        this.selectedModel = model as string;
        this.loadPersistedFiles();
        this.loadKnowledgeBaseItems();
      },
      error: (err) => {
        console.error('Initialization failed:', err);
        this.handleCriticalError('Component initialization failed.');
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private handleCriticalError(message: string): void {
    this.stateService.setKBError(message);
    this.stateService.setFilesError(message);
  }

  autoResize(event: Event): void {
    const textarea = event.target as HTMLTextAreaElement;
    textarea.style.height = 'auto';
    textarea.style.height = Math.min(textarea.scrollHeight, 120) + 'px';
  }

  openChatWithQuery(): void {
    if (this.searchQueryControl.trim()) {
      this.isChatOpen = true;
    }
  }

  closeChat(): void {
    this.isChatOpen = false;
    this.searchQueryControl = '';
  }

  // ===================================================================
  // FILE MANAGEMENT
  // ===================================================================

  handleDeleteIndexedFile(file: PersistedFileInfo): void {
    if (!file.id) return;

    Swal.fire({
      title: `Delete ${file.name}?`,
      text: 'The file and all indexed data will be permanently removed.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      confirmButtonText: 'Delete',
      cancelButtonText: 'Cancel'
    }).then((result) => {
      if (result.isConfirmed) {
        this.persistenceApiService.deleteFileRecord(file.id!, this.currentUserEmail, this.projectId)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: () => {
              this.handleFileDeleted();
              Swal.fire({ toast: true, position: 'top-end', icon: 'success', title: `${file.name} deleted`, showConfirmButton: false, timer: 3000 });
            },
            error: (err) => {
              console.error('Delete failed:', err);
              Swal.fire('Error', `Could not delete ${file.name}`, 'error');
            }
          });
      }
    });
  }

  handleIndexRequest(file: PersistedFileInfo): void {
    if (!file.id || file.status === 'Indexing') return;

    file.status = 'Indexing';
    file.message = 'Generating chunks & embeddings...';

    Swal.fire({ toast: true, position: 'top-end', showConfirmButton: false, timer: 4000, title: `Indexing started: ${file.name}`, icon: 'info' });

    this.persistenceApiService.indexFileChunksStream(file.id, this.currentUserEmail, this.projectId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (event) => {
          if (event.type === 'progress' && event.phase === 'chunking') {
            file.message = 'Splitting text into chunks...';
          } else if (event.type === 'progress' && event.phase === 'embedding') {
            file.message = `Embedding ${event.current}/${event.total}...`;
          } else if (event.type === 'complete' && event.data) {
            file.status = 'Success';
            file.isEmbedded = true;
            file.message = `Indexed: ${event.data.chunks_created} chunks created.`;
            Swal.fire({ toast: true, position: 'top-end', icon: 'success', title: 'Indexing complete', text: `${file.name} — ${event.data.chunks_created} chunks`, showConfirmButton: false, timer: 4000 });
            this.loadPersistedFiles();
          } else if (event.type === 'error') {
            file.status = 'Error';
            file.message = event.detail || 'Indexing failed.';
            Swal.fire('Indexing Error', `Could not process ${file.name}`, 'error');
          }
        },
        error: (err) => {
          console.error(err);
          file.status = 'Error';
          file.message = 'Indexing failed.';
          Swal.fire('Indexing Error', `Could not process ${file.name}`, 'error');
        }
      });
  }

  handleIndexAllRequest(): void {
    const filesToIndex = this.stateService.currentState.persistedFiles
      .filter(f => f.status === 'Success' && !f.isEmbedded);

    if (filesToIndex.length === 0) {
      Swal.fire('Up to Date', 'All valid files are already indexed.', 'info');
      return;
    }

    Swal.fire({
      title: 'Index All Files?',
      text: `This will process ${filesToIndex.length} documents in the background.`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: `Start Processing`
    }).then((result) => {
      if (result.isConfirmed) {
        filesToIndex.forEach(file => this.handleIndexRequest(file));
      }
    });
  }

  loadPersistedFiles(): void {
    this.stateService.setFilesLoading();
    this.persistenceApiService.listFiles(this.currentUserEmail, this.projectId)
      .pipe(
        map((files: FileRecordResponse[]): PersistedFileInfo[] => {
          if (!Array.isArray(files)) return [];
          return files.map(f => {
            const raw = f as any;
            return {
              id: f._id,
              name: f.name || 'Unnamed File',
              status: 'Success' as const,
              timestamp: new Date(f.timestamp),
              message: `Stored on ${new Date(f.timestamp).toLocaleDateString()}`,
              contentType: f.contentType as PersistedFileInfo['contentType'],
              uploadedBy: f.userName,
              isEmbedded: !!raw.is_embedded,
              contentLength: raw.content_length || 0,
            };
          });
        }),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: (mappedFiles) => {
          this.stateService.setFiles(mappedFiles);
        },
        error: (err) => {
          this.stateService.setFilesError(err.message || 'Failed to load file list.');
        }
      });
  }

  loadKnowledgeBaseItems(): void {
    this.stateService.setKBLoading();
    this.userStoryService.getKnowledgeBaseItems(this.currentUserEmail, this.projectId)
      .pipe(
        takeUntil(this.destroy$),
        map(items => this.processApiItems(items as any[]))
      )
      .subscribe({
        next: (items) => {
          this.stateService.setOriginalKnowledgeBaseItems(items);
          this.ambiguityKiItems = items
            .filter(i => i.source_type === 'ambiguity_resolution')
            .sort((a, b) => (b.lastModified || '').localeCompare(a.lastModified || ''));
          this.filterKiItems();
        },
        error: (err) => {
          this.stateService.setKBError('Failed to load knowledge base items.');
        }
      });
  }

  private processApiItems(items: any[]): KnowledgeBaseItem[] {
    if (!Array.isArray(items)) return [];
    return items.map(item => {
      if (item && item._id && typeof item._id === 'object' && item._id.$oid) {
        return { ...item, _id: item._id.$oid as string };
      } else if (item && item._id && typeof item._id !== 'string') {
        return { ...item, _id: String(item._id) };
      }
      return item;
    });
  }

  // ===================================================================
  // EVENT HANDLERS from Child Components
  // ===================================================================

  handleFileFilterChanged(file: PersistedFileInfo | null): void {
    this.stateService.setFileFilter(file);
  }

  handleFileUploaded(newFileRecord: FileRecordResponse): void {
    this.loadPersistedFiles();
  }

  handleFileDeleted(): void {
    this.loadKnowledgeBaseItems();
    this.loadPersistedFiles();
  }

  handleOpenSource(source: AgentChatSource): void {
    // Knowledge Items — find the matching KI and open its detail modal
    if (source.file_name === '[Knowledge Item]' || source.file_name === 'Knowledge Item') {
      const kiTitle = source.title || '';
      const ki = this.ambiguityKiItems.find(k =>
        k.title === kiTitle || (k.definition || '').includes(source.snippet?.slice(0, 50) || '')
      );
      if (ki) {
        this.openKiDetail(ki);
      } else {
        Swal.fire('Knowledge Item', source.snippet || source.evidence_quote || '', 'info');
      }
      return;
    }

    // Find the file in persistedFiles by name
    const file = this.stateService.currentState.persistedFiles
      .find(f => f.name === source.file_name);

    if (!file) {
      Swal.fire('File Not Found', `Could not find "${source.file_name}" in your documents.`, 'warning');
      return;
    }

    const openWithHighlight = (fileWithContent: PersistedFileInfo) => {
      const modalRef = this.modalService.open(FileContentViewerComponent, {
        size: 'xl', centered: true, scrollable: true, backdrop: 'static', windowClass: 'fixed-size-modal'
      });
      modalRef.componentInstance.fileInfo = fileWithContent;
      modalRef.componentInstance.currentUserEmail = this.currentUserEmail;
      modalRef.componentInstance.projectId = this.projectId;
      modalRef.componentInstance.selectedModel = this.selectedModel;

      // Add source highlight after component init
      setTimeout(() => {
        const quote = source.evidence_quote?.trim();
        console.log('[Agent Source] evidence_quote:', JSON.stringify(quote));
        console.log('[Agent Source] quote length:', quote?.length || 0);

        if (quote && quote.length > 5) {
          // Check if the quote actually exists in the document
          const docContent = fileWithContent.content || '';
          const foundInDoc = docContent.includes(quote);
          console.log('[Agent Source] quote found in document:', foundInDoc);

          if (!foundInDoc) {
            // Try a fuzzy match — first 40 chars
            const partial = quote.substring(0, 40);
            const partialFound = docContent.includes(partial);
            console.warn('[Agent Source] EXACT quote NOT found in document.');
            console.warn('[Agent Source] Partial match (first 40 chars):', partialFound, JSON.stringify(partial));
            console.warn('[Agent Source] Full document length:', docContent.length);
          }

          modalRef.componentInstance.mappedSnippetsForHighlighter = [
            ...modalRef.componentInstance.mappedSnippetsForHighlighter,
            {
              id: 'agent-source-' + Date.now(),
              textToFind: quote,
              cssClass: 'agent-source-highlight',
              tooltip: 'Source used by AI',
              data: { type: 'existing', originalText: quote }
            }
          ];
        } else {
          console.warn('[Agent Source] No evidence_quote available. Source:', JSON.stringify(source));
        }
      }, 200);

      modalRef.result.then(() => {}).catch(() => {});
    };

    if (file.content) {
      openWithHighlight(file);
    } else if (file.id) {
      Swal.fire({ title: 'Loading Document...', allowOutsideClick: false, didOpen: () => Swal.showLoading() });
      this.persistenceApiService.getFileRecord(file.id, this.currentUserEmail, this.projectId)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (fullRecord) => {
            Swal.close();
            if (fullRecord.content) {
              file.content = fullRecord.content;
              openWithHighlight(file);
            } else {
              Swal.fire('No Content', 'File has no text content.', 'info');
            }
          },
          error: (err) => {
            Swal.close();
            Swal.fire('Error', `Could not load file: ${err.message}`, 'error');
          }
        });
    }
  }

  // ===================================================================
  // KI DA RISOLUZIONI AMBIGUITA'
  // ===================================================================

  filterKiItems(): void {
    const q = this.kiSearchQuery.trim().toLowerCase();
    if (!q) {
      this.filteredKiItems = this.ambiguityKiItems;
    } else {
      this.filteredKiItems = this.ambiguityKiItems.filter(ki =>
        (ki.title || '').toLowerCase().includes(q) ||
        (ki.definition || '').toLowerCase().includes(q) ||
        (ki.answer || '').toLowerCase().includes(q) ||
        (ki.question || '').toLowerCase().includes(q)
      );
    }
    this.kiDisplayLimit = 10;
  }

  openKiDetail(ki: KnowledgeBaseItem, editMode = false): void {
    this.selectedKi = ki;
    this.selectedKiStory = null;
    this.isEditingKi = false;

    // Fetch story if story_debug_ref exists
    if (ki.story_debug_ref) {
      this.persistenceApiService.getStoryDebugLog(ki.story_debug_ref)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (entry) => { this.selectedKiStory = entry.story_text; },
          error: () => { this.selectedKiStory = null; }
        });
    }

    this.modalService.open(this.kiDetailModal, {
      size: 'lg', centered: true, scrollable: true, backdrop: 'static'
    });

    if (editMode) {
      this.startEditKi();
    }
  }

  startEditKi(): void {
    if (!this.selectedKi) return;
    this.isEditingKi = true;
    this.editKiData = {
      title: this.selectedKi.title || '',
      question: this.selectedKi.question || '',
      impact_text: this.selectedKi.impact_text || '',
      answer: this.selectedKi.answer || ''
    };
  }

  saveKi(): void {
    if (!this.selectedKi || this.isSavingKi) return;
    this.isSavingKi = true;

    const update: any = {
      userName: this.currentUserEmail,
      projectName: this.projectId,
      title: this.editKiData.title,
      question: this.editKiData.question,
      impact_text: this.editKiData.impact_text,
      answer: this.editKiData.answer,
      definition: this.editKiData.answer
    };

    this.userStoryService.updateKnowledgeBaseItem(this.currentUserEmail, this.projectId, this.selectedKi._id, update)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          const now = new Date().toISOString();
          Object.assign(this.selectedKi!, {
            title: this.editKiData.title,
            question: this.editKiData.question,
            impact_text: this.editKiData.impact_text,
            answer: this.editKiData.answer,
            definition: this.editKiData.answer,
            lastModified: now
          });
          this.isEditingKi = false;
          this.isSavingKi = false;
          this.ambiguityKiItems.sort((a, b) => (b.lastModified || '').localeCompare(a.lastModified || ''));
          this.loadKnowledgeBaseItems();
          Swal.fire({ toast: true, position: 'top-end', icon: 'success', title: 'KI updated', showConfirmButton: false, timer: 2000 });
        },
        error: (err) => {
          this.isSavingKi = false;
          Swal.fire('Error', `Could not update KI: ${err.message}`, 'error');
        }
      });
  }

  cancelEditKi(): void {
    this.isEditingKi = false;
  }

  deleteKi(ki: KnowledgeBaseItem, event?: Event): void {
    if (event) event.stopPropagation();

    Swal.fire({
      title: `Delete this KI?`,
      text: ki.title,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      confirmButtonText: 'Delete',
      cancelButtonText: 'Cancel'
    }).then((result) => {
      if (result.isConfirmed) {
        this.userStoryService.deleteKnowledgeBaseItem(this.currentUserEmail, this.projectId, ki._id)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: () => {
              this.ambiguityKiItems = this.ambiguityKiItems.filter(i => i._id !== ki._id);
              this.modalService.dismissAll();
              this.loadKnowledgeBaseItems();
              Swal.fire({ toast: true, position: 'top-end', icon: 'success', title: 'KI deleted', showConfirmButton: false, timer: 2000 });
            },
            error: (err) => {
              Swal.fire('Error', `Could not delete KI: ${err.message}`, 'error');
            }
          });
      }
    });
  }

  // ===================================================================
  // FILE VIEWER
  // ===================================================================

  handleFileSelectedForView(file: PersistedFileInfo): void {
    const openModal = (fileWithContent: PersistedFileInfo) => {
      const modalRef = this.modalService.open(FileContentViewerComponent, {
        size: 'xl', centered: true, scrollable: true, backdrop: 'static', windowClass: 'fixed-size-modal'
      });
      modalRef.componentInstance.fileInfo = fileWithContent;
      modalRef.componentInstance.currentUserEmail = this.currentUserEmail;
      modalRef.componentInstance.projectId = this.projectId;
      modalRef.componentInstance.selectedModel = this.selectedModel;
      if (modalRef.componentInstance.indexRequested) {
        modalRef.componentInstance.indexRequested.subscribe(() => {
          this.handleIndexRequest(fileWithContent);
        });
      }

      modalRef.result.then(result => {
        if (result === 'KIs_CREATED') {
          this.loadKnowledgeBaseItems();
          this.loadPersistedFiles();
        }
      }).catch(() => {});
    };

    if (file.content) {
      openModal(file);
    } else if (file.id) {
      Swal.fire({ title: 'Loading Content...', allowOutsideClick: false, didOpen: () => Swal.showLoading() });
      this.persistenceApiService.getFileRecord(file.id, this.currentUserEmail, this.projectId)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (fullRecord) => {
            Swal.close();
            if (fullRecord.content) {
              file.content = fullRecord.content;
              openModal(file);
            } else {
              Swal.fire('No Content', 'File record exists but has no text content.', 'info');
            }
          },
          error: (err) => {
            Swal.close();
            Swal.fire('Error Loading Content', `Could not fetch file content: ${err.message}`, 'error');
          }
        });
    }
  }
}
